import axios from 'axios';
import api from '@/api/api.js';

export default {
  data() {
    return {
      telemetrySessionId: '',
      telemetryBuffer: [],
      telemetryHistory: [],
      telemetrySending: false,
      telemetryFlushTimer: null,
      telemetryPersistTimer: null,
      telemetryFlushSize: 20,
      telemetryMaxBuffer: 500,
      telemetryFlushIntervalMs: 10000,
      telemetryEndpoint: process.env.VUE_APP_UPLOAD_TELEMETRY_URL || '',
    };
  },
  beforeDestroy() {
    this.cleanupPhotoUploadTelemetry();
  },
  beforeUnmount() {
    this.cleanupPhotoUploadTelemetry();
  },
  methods: {
    logUploadEvent(event, details = {}) {
      const record = {
        event,
        timestamp: new Date().toISOString(),
        telemetry_session_id: this.telemetrySessionId || null,
        upload_id: this.uuidValue || null,
        wp_run_id: this.selectedEvent?.id ?? null,
        upload_phase: this.uploadPhase,
        upload_status: this.uploadStatus,
        online: typeof navigator !== 'undefined' ? navigator.onLine : null,
        visibility: typeof document !== 'undefined' ? document.visibilityState : null,
        confirmed_files: this.confirmedPhotosCount,
        total_files: this.imageCount,
        ...details,
      };

      this.telemetryBuffer.push(record);
      this.telemetryHistory.push(record);
      if (this.telemetryHistory.length > 200) {
        this.telemetryHistory.splice(0, this.telemetryHistory.length - 200);
      }
      if (this.telemetryBuffer.length > this.telemetryMaxBuffer) {
        this.telemetryBuffer.splice(0, this.telemetryBuffer.length - this.telemetryMaxBuffer);
      }

      this.scheduleTelemetryPersist();
      if (this.telemetryEndpoint && this.telemetryBuffer.length >= this.telemetryFlushSize) {
        this.scheduleTelemetryFlush();
      } else if (this.telemetryEndpoint && !this.telemetryFlushTimer) {
        this.telemetryFlushTimer = setTimeout(() => {
          this.telemetryFlushTimer = null;
          this.flushUploadTelemetry();
        }, this.telemetryFlushIntervalMs);
      }

      if (process.env.NODE_ENV !== 'production') {
        console.debug('[photo-upload]', record);
      }
    },
    serializeUploadError(error) {
      if (!error) {
        return null;
      }
      return {
        message: error.message || null,
        code: error.code || null,
        http_status: error.response?.status || null,
        cancelled: axios.isCancel(error),
        retry_after: error.response?.headers?.['retry-after'] || null,
        request_id: this.getResponseRequestId(error.response),
      };
    },
    getResponseRequestId(response) {
      if (!response?.headers) {
        return null;
      }

      const getHeader = (name) => {
        if (typeof response.headers.get === 'function') {
          return response.headers.get(name);
        }
        return response.headers[name];
      };

      return getHeader('x-request-id') ||
        getHeader('x-correlation-id') ||
        getHeader('request-id') ||
        null;
    },
    scheduleTelemetryPersist() {
      if (this.telemetryPersistTimer) {
        return;
      }
      this.telemetryPersistTimer = setTimeout(() => {
        this.telemetryPersistTimer = null;
        try {
          localStorage.setItem(
            'runpixie:last-upload-telemetry',
            JSON.stringify(this.telemetryHistory.slice(-200))
          );
        } catch {
          // Diagnostics must never influence the upload itself.
        }
      }, 1500);
    },
    scheduleTelemetryFlush(force = false) {
      if (!this.telemetryEndpoint) {
        return;
      }
      if (force) {
        this.flushUploadTelemetry();
        return;
      }
      if (this.telemetryFlushTimer) {
        return;
      }
      this.telemetryFlushTimer = setTimeout(() => {
        this.telemetryFlushTimer = null;
        this.flushUploadTelemetry();
      }, 500);
    },
    async flushUploadTelemetry() {
      if (!this.telemetryEndpoint || this.telemetrySending || !this.telemetryBuffer.length) {
        return;
      }

      const events = this.telemetryBuffer.splice(0, Math.min(50, this.telemetryBuffer.length));
      this.telemetrySending = true;
      try {
        await api.sendUploadTelemetry(events);
      } catch {
        this.telemetryBuffer = [...events, ...this.telemetryBuffer].slice(-this.telemetryMaxBuffer);
      } finally {
        this.telemetrySending = false;
        if (this.telemetryBuffer.length >= this.telemetryFlushSize) {
          this.scheduleTelemetryFlush();
        }
      }
    },
    cleanupPhotoUploadTelemetry() {
      if (this._telemetryCleanupDone) {
        return;
      }
      this._telemetryCleanupDone = true;
      if (this.telemetryFlushTimer) {
        clearTimeout(this.telemetryFlushTimer);
        this.telemetryFlushTimer = null;
      }
      if (this.telemetryPersistTimer) {
        clearTimeout(this.telemetryPersistTimer);
        this.telemetryPersistTimer = null;
      }
      this.flushUploadTelemetry();
    },
  },
};
